import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useStorefront } from '@/hooks/useStorefront';
import { useLanguage } from '@/hooks/useLanguage';
import {
  resolveSeoText,
  seoOverrideKey,
  EMPTY_SEO_OVERRIDE_MAP,
  type SeoField,
  type SeoScope,
  type SeoOverrideMap,
} from '@/lib/seoOverrides';

/**
 * Loads all SEO overrides for the (non-CH) storefront + locale in ONE query
 * and caches the resulting Map. Not exported: callers use `useSeoText`.
 *
 * This function MUST NOT be invoked on the CH branch — the CH fastpath in
 * `useSeoText` returns before we get here, so useQuery is never registered on
 * CH (no cache entry, no listener, no subscription).
 */
function useSeoOverridesMap(): SeoOverrideMap {
  const { storefront } = useStorefront();
  const { locale } = useLanguage();
  const query = useQuery({
    queryKey: ['seo_overrides', storefront, locale],
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('seo_overrides')
        .select('scope, scope_key, storefront, locale, field, value')
        .eq('storefront', storefront)
        .eq('locale', locale);
      if (error) throw error;
      const m: SeoOverrideMap = new Map();
      for (const r of (data as any[]) ?? []) {
        if (typeof r?.value !== 'string' || r.value.length === 0) continue;
        m.set(
          seoOverrideKey({
            scope: r.scope,
            scope_key: r.scope_key,
            field: r.field,
            locale: r.locale,
          }),
          r.value,
        );
      }
      return m;
    },
  });
  return query.data ?? EMPTY_SEO_OVERRIDE_MAP;
}

/**
 * Returns the SEO text to display for a given (scope, key, field) — either an
 * admin-authored override from `seo_overrides`, or the base string passed in.
 *
 * CH-FASTPATH — ZERO READ:
 *   For storefront === 'ch' this hook returns `base` immediately.
 *     * NO Supabase fetch
 *     * NO react-query subscription (useQuery is not called at all)
 *     * NO cache entry, no listeners, no revalidation
 *
 * The conditional call to useSeoOverridesMap() below is safe: `storefront`
 * is derived from window.location.hostname and stable for the mount, so hook
 * order never changes for a given component instance.
 */
export function useSeoText(
  base: string,
  scope: SeoScope,
  scope_key: string,
  field: SeoField,
): string {
  const { storefront } = useStorefront();
  if (storefront === 'ch') return base; // CH-FASTPATH — before any hook/query.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const overrides = useSeoOverridesMap();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { locale } = useLanguage();
  return resolveSeoText(base, storefront, { scope, scope_key, field, locale }, overrides);
}